import { Box, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";

/**
 * OAuth callback handler for Google Calendar.
 * This component receives the OAuth redirect, extracts the authorization code,
 * and sends it back to the parent window (the settings page).
 */
function GoogleCalendarCallback({ t }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    // Extract OAuth parameters from URL
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const state = searchParams.get("state"); // Contains organization ID

    if (error) {
      // OAuth error occurred
      setStatus("error");

      // Send error to parent window
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "google_calendar_callback",
            error: error,
            state: state,
          },
          window.location.origin
        );
      }

      // Close window after short delay
      setTimeout(() => {
        window.close();
      }, 2000);

      return;
    }

    if (code) {
      // Success - send code to parent window
      setStatus("success");

      if (window.opener) {
        window.opener.postMessage(
          {
            type: "google_calendar_callback",
            code: code,
            state: state,
          },
          window.location.origin
        );
      }

      // Close window after short delay
      setTimeout(() => {
        window.close();
      }, 1000);

      return;
    }

    // No code or error - something went wrong
    setStatus("error");
    setTimeout(() => {
      window.close();
    }, 2000);
  }, [searchParams]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 4,
      }}
    >
      {status === "processing" && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>{t("googleCalendar.processingAuth")}</Typography>
        </>
      )}

      {status === "success" && (
        <>
          <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
            {t("googleCalendar.authSuccess")}
          </Typography>
          <Typography color="text.secondary">
            {t("googleCalendar.closingWindow")}
          </Typography>
        </>
      )}

      {status === "error" && (
        <>
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            {t("googleCalendar.authError")}
          </Typography>
          <Typography color="text.secondary">
            {t("googleCalendar.closingWindow")}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default withTranslation()(GoogleCalendarCallback);
